/**
 * Customer Portal — route configuration.
 * Public routes: /login, /forgot-password, /reset-password, /health, /unauthorized
 * Protected routes: /dashboard, /locations, /locations/:id, /tickets, /documents, /billing
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { HealthPage } from './pages/HealthPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { DashboardPage } from './pages/DashboardPage';
import { LocationsPage } from './pages/LocationsPage';
import { LocationDetailPage } from './pages/LocationDetailPage';
import { TicketsPage } from './pages/TicketsPage';
import { TicketDetailPage } from './pages/TicketDetailPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { BillingPage } from './pages/BillingPage';
import { TrendsPage } from './pages/TrendsPage';
import { StaffViewAsPage } from './pages/StaffViewAsPage';
import { CrmEntryPage } from './pages/CrmEntryPage';
import { RootRoute } from './components/RootRoute';
import { STAFF_VIEW_AS_PATH } from './contexts/ViewAsContext';

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      {/* `/` doubles as the magic-link / invite return target (PKCE
          redirects to the site root). RootRoute forwards an
          authenticated session to /dashboard and shows a spinner
          while the code exchange is in flight. */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Password recovery — /reset-password is the redirect target of
          reset emails and must stay public (session is established from
          the link itself). */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/health" element={<HealthPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected routes — auth required */}
      <Route element={<ProtectedRoute />}>
        {/* Staff-only picker: lives outside AppLayout because the layout
            redirects staff here until a portal user has been chosen. */}
        <Route path={STAFF_VIEW_AS_PATH} element={<StaffViewAsPage />} />
        <Route element={<AppLayout />}>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="crm/account/:zohoAccountId" element={<CrmEntryPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="locations/:id" element={<LocationDetailPage />} />
          <Route path="locations/:id/systems" element={<LocationDetailPage />} />
          <Route path="locations/:id/network" element={<LocationDetailPage />} />
          <Route path="locations/:id/services" element={<LocationDetailPage />} />
          <Route path="locations/:id/documents" element={<LocationDetailPage />} />
          <Route path="locations/:id/activity" element={<LocationDetailPage />} />
          <Route path="locations/:id/reports" element={<LocationDetailPage />} />
          <Route path="locations/:id/tickets" element={<TicketsPage />} />
          <Route path="locations/:id/tickets/:ticketId" element={<TicketDetailPage />} />
          <Route path="locations/:id/trends" element={<TrendsPage />} />
          <Route path="trends" element={<TrendsPage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="billing" element={<BillingPage />} />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
